import React, { useEffect, useState } from "react"
import "@coreui/coreui/dist/css/coreui.min.css"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { ChevronRight, X } from "lucide-react"
import { useWorldData } from "../hooks/useWorldData"
import { useWorldMaps } from "../hooks/useWorldMaps"
import CustomSwitch from "../components/CustomSwitch"
import './SideMenu.css'

import {
  CSidebar,
  CSidebarNav,
  CNavItem,
  CNavGroup,
} from "@coreui/react"

import { CIcon } from "@coreui/icons-react"
import {
  cilMap,
  cilFile,
  cilGlobeAlt,
  cilCircle,
  cilCheckCircle,
} from "@coreui/icons"

const menuItems = [
  { name: "Map", route: "/map", icon: cilMap },
  { name: "Insight Reports", route: "/reports", icon: cilFile },
]

const LARGE_SCREEN_QUERY = "(min-width: 993px)"

const useIsLargeScreen = () => {
  const [isLarge, setIsLarge] = useState(
    () => typeof window !== "undefined" && window.matchMedia(LARGE_SCREEN_QUERY).matches
  )

  useEffect(() => {
    const mq = window.matchMedia(LARGE_SCREEN_QUERY)
    const handler = (e) => setIsLarge(e.matches)
    mq.addEventListener("change", handler)
    return () => mq.removeEventListener("change", handler)
  }, [])

  return isLarge
}

const Sidebar = ({
    compareMode,
    setCompareMode,
    compareCountries,
    setCompareCountries
  })=> {

  const { countries, regions } = useWorldData()

  const { availableCountries, countryInfo } = useWorldMaps(countries, regions)
  const location = useLocation();

  const navigate = useNavigate()
  const isLargeScreen = useIsLargeScreen()

  const [mobileOpen, setMobileOpen] = useState(false)

  const activeCountry = location.pathname.startsWith("/country/")
    ? location.pathname.split("/")[2]
    : null;

  useEffect(() => {
    if (!compareMode) {
      setCompareCountries([])
    }
  }, [compareMode, setCompareCountries])

  // Close the drawer automatically after navigating somewhere.
  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  const sidebarBody = (
    <>
      {/* ITEMS (map) */}
      <CSidebarNav>
        {menuItems.map((item) => (
          <CNavItem
            key={item.route}
            href="#"
            className={location.pathname === item.route ? "active-country" : ""}
            onClick={(e) => {
              e.preventDefault()
              navigate(item.route)
            }}
          >
            <CIcon className="nav-icon" icon={item.icon} />
            <span className="nav-link-text">{item.name}</span>
          </CNavItem>
        ))}

        {/* COMPARE */}
        <CNavItem>
          <div className="nav-link nav-toggler-row">
            <span className="nav-icon">
              <CIcon icon={compareMode ? cilCheckCircle : cilCircle} />
            </span>

            <span className="nav-link-text">Compare</span>

            <span className="nav-switch-end">
              <CustomSwitch
                size="small"
                checked={compareMode}
                onChange={() => setCompareMode(prev => !prev)}
              />
            </span>
          </div>
        </CNavItem>

        {/* COUNTRIES */}
        <CNavGroup
          toggler={
            <>
              <CIcon className="nav-icon" icon={cilGlobeAlt} />
              <span className="nav-link-text">Countries</span>
            </>
          }
        >
          {regions.map((region) => (
            <CNavGroup
              key={region.code}
              toggler={
                <>
                  <span className="nav-link-text" title={region.name}>{region.name}</span>
                </>
              }
            >
              {(availableCountries[region.code] || []).map((countryCode) => {
                const country = countries.find((c) => c.code === countryCode)
                const isSelected = compareCountries.includes(countryCode)

                return (
                  <CNavItem key={countryCode}>
                    <Link
                      to={`/country/${countryCode}`}
                      state={{ countryName: country?.name }}
                      className={`nav-link 
                          ${
                            !compareMode && activeCountry === countryCode
                              ? "active-country"
                              : ""
                          }
                          ${
                            compareMode && activeCountry === countryCode
                              ? "active-country-compare"
                              : ""
                          }
                          ${compareMode && isSelected ? "selected-country" : ""}
                        `}
                      onClick={(e) => {
                      if (compareMode) {
                        e.preventDefault()

                        setCompareCountries(prev => {
                          const isActiveRoute = activeCountry === countryCode

                          // do nothing if it's current route country
                          if (isActiveRoute) return prev

                          const isSelected = prev.includes(countryCode)

                          // deselect if already selected
                          if (isSelected) {
                            return prev.filter(c => c !== countryCode)
                          }

                          // otherwise replace selection
                          return [countryCode]
                        })

                        return
                      }
                    }}
                    >
                      <span className="nav-link-text">
                        {country?.name || countryCode}
                      </span>
                    </Link>
                  </CNavItem>
                )
              })}
            </CNavGroup>
          ))}
        </CNavGroup>

      </CSidebarNav>
    </>
  )

  if (isLargeScreen) {
    return (
      <CSidebar className="custom-sidebar" visible unfoldable>
        {sidebarBody}
      </CSidebar>
    )
  }

  // MEDIUM & SMALL SCREENS: a fully custom plain <div> drawer
  return (
    <>
      {!mobileOpen && (
        <button
          className="sidebar-mobile-handle"
          onClick={() => setMobileOpen(true)}
          aria-label="Open menu"
        >
          <ChevronRight size={18} />
        </button>
      )}

      {mobileOpen && (
        <div
          className="sidebar-mobile-backdrop"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <div className={`custom-sidebar${mobileOpen ? " mobile-open" : ""}`}>
        <button
          className="sidebar-mobile-close"
          onClick={() => setMobileOpen(false)}
          aria-label="Close menu"
        >
          <X size={16} />
        </button>

        {sidebarBody}
      </div>
    </>
  )
}

export default Sidebar
